import React, { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import {
  apiClient,
  isSilenceToaster,
  API_PATHS,
  fetchCustomerConfiguration,
  CUSTOMER_CONFIGURATION_TYPES,
  trackGoogleAnalyticsEvent,
  ANALYTICS_CONSTS
} from '@cyberint/common-utils';
import { useSnackbars } from '@cyberint/dsc';
import * as S from '../components/LitmosNotifications/styled';
import { REGISTRATION_NOT_COMPLETED, getText } from '../components/LitmosNotifications/utils';

const Icon = () => (
  <S.IconWrapper>
    <S.Icon />
  </S.IconWrapper>
);

export const useLitmosNotificationsBase = userInfo => {
  const { successSnackbar, errorSnackbar, closeNotification } = useSnackbars();
  const hasFetchedRef = useRef(false);

  const sendGAEvent = event =>
    trackGoogleAnalyticsEvent({
      [ANALYTICS_CONSTS.GA_CATEGORY]: ANALYTICS_CONSTS.GA_CATEGORY_INTEGRATIONS,
      [ANALYTICS_CONSTS.GA_ACTION]: ANALYTICS_CONSTS.gaActionPrefixClickedOn(event)
    });

  const sendLitmosRegNotification = async () => {
    if (!userInfo) return;

    try {
      const body = {
        selectedCustomerId: userInfo.customer._id,
        server: userInfo.customer.server,
        email: userInfo.username,
        fullname: userInfo.fullname
      };
      await apiClient.fesClient.post(API_PATHS.LITMOS_REGISTRATION_NOTIFICATION, body);
      successSnackbar(<Trans i18nKey="integrations.litmos.email_notification_success" />);
    } catch (err) {
      errorSnackbar(<Trans i18nKey="integrations.litmos.email_notification_error" />);
    }
  };

  const showNotification = notification => {
    const { notification_id: id, notification_type: type, course_name: courseName, link } = notification;

    const onClose = () => {
      apiClient.defaultClient.post(`${API_PATHS.USER_NOTIFICATIONS}/lms/ack`, { notification_id: id });
      sendGAEvent(`close litmos notification ${type}`);
    };

    const onClick = () => {
      closeNotification(id);
      type === REGISTRATION_NOT_COMPLETED && sendLitmosRegNotification();
      sendGAEvent(`litmos notification link ${type}`);
    };

    const { title, message } = getText({ type, courseName, link, onClick });
    const snackbarOptions = {
      preventDuplicate: true,
      key: id,
      persist: true,
      icon: <Icon />,
      color: 'var(--indigo)',
      title,
      onClose
    };

    errorSnackbar(message, snackbarOptions);
  };

  const fetchLmsNotifications = async () => {
    try {
      const response = await apiClient.defaultClient.get(`${API_PATHS.USER_NOTIFICATIONS}/lms`);
      const notification = response?.data?.data;

      !!notification && showNotification(notification);
    } catch (err) {
      // do nothing
    }
  };

  useEffect(() => {
    // Wait for a session before consuming the once-per-mount guard — the shell
    // renders with a null user until the session loads.
    if (!userInfo || hasFetchedRef.current || isSilenceToaster()) return;
    hasFetchedRef.current = true;

    const onUseEffect = async () => {
      try {
        const res = await fetchCustomerConfiguration({
          filters: {
            customer_id: [userInfo.customer._id],
            configuration_type: [CUSTOMER_CONFIGURATION_TYPES.ELEARNING_PLATFORMS]
          }
        });
        const { customer_configurations: configs } = res.data.data;
        const litmosEnabled = configs[0].data.litmos_notifications_enabled;

        litmosEnabled && fetchLmsNotifications();
      } catch (err) {
        // do nothing
      }
    };

    onUseEffect();
  }, [userInfo]);
};
